import { Loading } from 'quasar'
import { route } from 'quasar/wrappers'
import { pinia } from 'src/modules/pinia.js'
import { decodeJwtOrNull, isJwtExpired } from 'src/utils/jwt.js'
import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router'

import routes from './routes'

const createHistory = process.env.SERVER ? createMemoryHistory : process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHistory

export const Router = createRouter({
  scrollBehavior: () => ({ left: 0, top: 0 }),
  routes,

  // Leave this as is and make changes in quasar.conf.js instead!
  // quasar.conf.js -> build -> vueRouterMode
  // quasar.conf.js -> build -> publicPath
  history: createHistory(process.env.VUE_ROUTER_BASE),
})

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(async function (/* { store, ssrContext } */) {
  const authStore = useAuthStore(pinia)
  const { accessToken, refreshToken } = storeToRefs(authStore)

  Router.beforeEach(async (to, from, next) => {
    Loading.show()

    await authStore.getPromise()

    // If no authentication is needed for this page, continue
    if (to.meta?.requiresAuth === false) {
      next()
    }
    // Otherwise check for access token validity
    else {
      // If no token is present, go to login
      if (!accessToken.value) {
        // If the "to" route is already the login page, dont redirect there as this is already happening
        if (to.name !== 'login') {
          return next({ name: 'login' })
        }
      } else {
        let decodedAccessToken = decodeJwtOrNull(accessToken.value)
        // If the token is not properly parsed or has expired, go to login
        if (isJwtExpired(accessToken.value)) {
          if (isJwtExpired(refreshToken.value)) {
            await authStore.fullLogout(false)
            return next({ name: 'login' })
          } else {
            // Try to refresh the AT first
            const newAccessToken = await authStore.refreshAccessToken()
            decodedAccessToken = decodeJwtOrNull(newAccessToken)
            if (isJwtExpired(newAccessToken)) {
              return next({ name: 'login' })
            }
          }
        }

        // If MFA is required, check if MFA is present in the token
        if (to.meta?.requiresMFA !== false && decodedAccessToken?.mfa !== true) {
          await authStore.fullLogout(false)
          return next({ name: 'login' })
        }
      }

      return next()
    }
  })
  Router.afterEach((to, from) => {
    Loading.hide()
  })

  return Router
})
